import { deferred } from '@berlin-front/common/utils';

type Close =
  /**
   * 排他処理ブロックを閉じます。
   */
  () => void;

/**
 * 非同期処理における排他制御の管理を行います。
 */
export default class ConcurrentManager {
  private lastRegisteredPromise: Promise<void> | undefined = undefined;

  // eslint-disable-next-line class-methods-use-this
  private rejectCurrentPromise: (error?: unknown) => void = () => {};

  /**
   * 初期状態に戻す。
   */
  private reset() {
    this.lastRegisteredPromise = undefined;
    this.rejectCurrentPromise = () => {};
  }

  /**
   * 排他処理を開始したいタイミングでこれを呼び、awaitをかけます。
   * すると、先行する排他処理が完了するまで処理がブロックされます。。
   *
   * 戻り値としてOk関数を返しますが、排他処理を終了したいタイミングでOk関数を呼んでください。
   * waitの実行後、必ずokが実行される必要があります。
   *
   * exampleに正しい使用法を書くので参考にすること。
   *
   * @example
   *   async () => {
   *     // 排他制御ブロックの開始。
   *     // どこかで`concurretManager.reject()`が実行されたりすると、制御が取れずここで例外が発生する可能性もある。
   *     const close = await concurrentManager.start();
   *     try {
   *       await someAsyncFunction1();
   *       await someAsyncFunction2();
   *       await someAsyncFunction3();
   *     } catch (e) {
   *       // たとえば処理失敗時にそれ以降の排他処理をまとめて失敗させたいならこんなふうにすればいい。
   *       concurretManager.reject(e);
   *       // rethrow
   *       throw e;
   *     } finally {
   *       // 排他処理ブロックの終了。waitの実行とokの実行完了までは他の排他処理ブロックは実行されません。
   *       close();
   *     }
   *   }
   *
   * @returns
   */
  public async start(): Promise<Close> {
    const [promise, resolve, reject] = deferred<void>();
    /** 1コ前に登録されたpromise */
    const prevPromise = this.lastRegisteredPromise;
    this.lastRegisteredPromise = promise;
    if (prevPromise) {
      void (async () => {
        try {
          await prevPromise;
          // 一個まえのpromiseが通ったら、このpromiseをrejectの起点にする。
          this.rejectCurrentPromise = reject;
        } catch (e) {
          // 1個まえのpromiseが失敗したら、このpromiseも巻き込まれる形で失敗する。
          reject(e);
          // 最後のpromiseがrejectされたら初期化する。
          if (this.lastRegisteredPromise === promise) {
            this.reset();
          }
        }
      })();
    } else {
      // 直前のpromiseがないなら、このpromiseがrejectの起点になる。
      this.rejectCurrentPromise = reject;
    }
    const close = () => {
      void (async () => {
        if (prevPromise) {
          try {
            await prevPromise;
            resolve();
          } catch {
            // ここではなにもしない。失敗の伝播はもう定義済み。
          }
        } else {
          resolve();
        }
        // 最後のpromiseが正常系に処理されたら初期化する。
        if (this.lastRegisteredPromise === promise) {
          this.reset();
        }
      })();
    };
    if (prevPromise) {
      // ok関数を返すのは、直前のpromiseが通ってから。
      // waitを多重呼び出しした場合は先に実行したwaitの返すok関数が呼ばれるまで、ここで処理がブロックされる。
      await prevPromise;
    }
    return close;
  }

  /**
   * waitで待ち状態になってる排他処理を失敗させます。
   *
   * どこかで `await concurrentManager.wait()` を行っている場合、これが呼ばれたタイミングで例外が発生し、waitが失敗します。
   * @param error
   */
  public reject(error?: unknown): void {
    this.rejectCurrentPromise(error);
    this.lastRegisteredPromise = undefined;
  }
}
